import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { ChevronRight, Settings } from "lucide-react";

export default function TournamentAdminAccess({ tournamentId, busy: tournamentBusy, onNotice, onEdit }) {
  const [owner, setOwner] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    const [tournamentResult, adminsResult] = await Promise.all([
      supabase.from("tournaments").select("creator").eq("id", tournamentId).single(),
      supabase.from("tournament_admins").select("id,profile_id,role").eq("tournament_id", tournamentId),
    ]);
    if (tournamentResult.error) throw tournamentResult.error;
    if (adminsResult.error) throw adminsResult.error;
    const creatorId = tournamentResult.data.creator;
    const ids = [...new Set([creatorId, ...(adminsResult.data || []).map((row) => row.profile_id)].filter(Boolean))];
    const profilesResult = ids.length ? await supabase.from("profiles").select("id,username,name,surname,profile_img").in("id", ids) : { data: [], error: null };
    if (profilesResult.error) throw profilesResult.error;
    const profiles = new Map((profilesResult.data || []).map((profile) => [profile.id, profile]));
    setOwner(profiles.get(creatorId) || null);
    setAdmins((adminsResult.data || []).filter((row) => row.profile_id !== creatorId).map((row) => ({ ...row, profile: profiles.get(row.profile_id) })));
  }, [tournamentId]);
  useEffect(() => {
    let active = true;
    load().catch((loadError) => { if (active) setError(loadError.message); });
    return () => { active = false; };
  }, [load]);
  const add = async (event) => {
    event.preventDefault();
    if (!username.trim() || busy || tournamentBusy) return;
    setBusy(true);
    setError("");
    try {
      const { data: candidate, error: lookupError } = await supabase.from("profiles").select("id").ilike("username", username.trim()).maybeSingle();
      if (lookupError) throw lookupError;
      if (!candidate) throw new Error("Username not found.");
      const { error: insertError } = await supabase.from("tournament_admins").insert({ tournament_id: tournamentId, profile_id: candidate.id, role: "admin" });
      if (insertError) throw insertError;
      setUsername("");
      await load();
      onNotice("Admin added.");
    } catch (saveError) { setError(saveError.message); }
    finally { setBusy(false); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove @${row.profile?.username || "this user"} as a tournament admin?`)) return;
    setBusy(true);
    setError("");
    try {
      const { error: removeError } = await supabase.from("tournament_admins").delete().eq("id", row.id);
      if (removeError) throw removeError;
      await load();
      onNotice("Admin removed.");
    } catch (saveError) { setError(saveError.message); }
    finally { setBusy(false); }
  };
  const label = (profile) => [profile?.name, profile?.surname].filter(Boolean).join(" ") || profile?.username || "Admin";
  return <section className="flow-admin-access">
    {onEdit && <div className="flow-admin-manage"><h2>Manage tournament</h2><button type="button" onClick={onEdit}><span className="flow-admin-manage-icon"><Settings size={18} aria-hidden="true" /></span><span><strong>Edit details</strong><small>Name, venue and event setup</small></span><ChevronRight size={18} aria-hidden="true" /></button></div>}
    <h2>Admin access · {admins.length + 1}</h2>
    <p>Owners and admins can manage tournament access, games and results.</p>
    {error && <p role="alert" className="flow-error">{error}</p>}
    {owner && <div className="flow-access-person"><span className="flow-access-avatar">{owner.profile_img ? <img src={owner.profile_img} alt="" /> : label(owner).slice(0,1)}</span><span><strong>{label(owner)}</strong><small>@{owner.username}</small></span><b>Owner</b></div>}
    <form onSubmit={add} className="flow-access-add"><h3>Add admin</h3><label className="flow-field">Admin username<input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Username" autoComplete="off" /></label><button disabled={!username.trim() || busy || tournamentBusy}>Add admin</button></form>
    <h3>Other admins · {admins.length}</h3>
    {admins.map((row) => <div key={row.id} className="flow-access-person"><span className="flow-access-avatar">{row.profile?.profile_img ? <img src={row.profile.profile_img} alt="" /> : label(row.profile).slice(0,1)}</span><span><strong>{label(row.profile)}</strong><small>@{row.profile?.username || "unknown"}</small></span><b>Admin</b><button type="button" className="secondary" disabled={busy || tournamentBusy} onClick={() => remove(row)} aria-label={`Remove ${label(row.profile)} as tournament admin`}>Remove</button></div>)}
    {!admins.length && <p>No other admins yet.</p>}
  </section>;
}
